import * as React from "react";

import { PolicyProvider, PolicyProviderContext } from "../context/PolicyProvider";

import { StatelessPolicy } from "./StatelessPolicy";
import { toaster } from "kanopi-components/ToastContainer";
import { useCovers } from "../../quoting/hooks/useCovers";
import { useGlobal } from "kanopi-components/useGlobal";
import { usePolicy } from "../hooks/usePolicy";

function Policy(props: {
  [x: string]: unknown;
  policyId: TPolicy["id"];
  editing: boolean;
}) {
  const { policyId, editing, ...rest } = props;
  const [
    policy,
    { repriceQuote, pricingFailed, policyLoading, priceLoading, fetchPolicy },
  ] = usePolicy(policyId);
  const [, setTrackingData] = useGlobal("tracking");
  const [coverSections, setCoverSections] = React.useState<TCoverSection[]>();
  const [covers, { loading: coversLoading }] = useCovers();
  const renewalBannerMessage = (latestRenewal) => {
    return `Your policy will auto renew on ${new Date(
      policy?.finishDate
    ).toLocaleDateString()}.
    Your new premium will be $${latestRenewal.quote?.riskProfile?.periodicPrice}.
    Please review your policy details and update them if needed.`;
  };
  React.useEffect(() => {
    if (editing && !coversLoading) {
      setCoverSections(covers);
    } else {
      setCoverSections(policy?.quote?.covers);
    }
  }, [policy, coversLoading, covers]);

  React.useEffect(() => {
    if (!policyLoading && policy?.autoRenewEnabled && policy?.latestRenewal?.quote) {
      toaster({
        kind: "INFO",
        content: renewalBannerMessage(policy?.latestRenewal),
        key: "auto-renewal-notification-toast",
      });
    } else if (
      policy?.quote?.product?.autoRenewAllowed &&
      !policy?.autoRenewEnabled &&
      policy?.isWithinRenewalPeriod
    ) {
      toaster({
        kind: "INFO",
        content:
          "Your policy is about to expire. To renew the policy, please update the policy and turn on 'Auto renew'.",
        key: "auto-renewal-disabled-notification-toast",
      });
    }
    if (!policyLoading && !coversLoading) {
      setTrackingData({
        riskProfile: policy?.riskProfile,
        enabledCoverSections: coverSections?.map((cover: TCoverSection) => cover.title),
      });
    }
  }, [policyLoading, policy]);

  return (
    <PolicyProvider policy={policy}>
      <PolicyProviderContext.Consumer>
        {(policyContext) => (
          <StatelessPolicy
            {...rest}
            riskProfile={
              editing && policy?.draftUpdate?.riskProfile
                ? policy?.draftUpdate?.riskProfile
                : policy?.riskProfile
            }
            policy={policy}
            coverSections={coverSections}
            loading={policyLoading}
            editing={editing}
            inactiveCovers={policyContext?.inactiveCovers}
            priceLoading={priceLoading}
            pricingFailed={pricingFailed}
            repriceQuote={repriceQuote}
            fetchPolicy={fetchPolicy}
          />
        )}
      </PolicyProviderContext.Consumer>
    </PolicyProvider>
  );
}

export { Policy };
